import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormPageLayout from "../../components/common/formkit/FormPageLayout";
import { Section, TextField, TextAreaField, SelectField, CurrencyField } from "../../components/common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../components/common/formkit/formHelpers";
import { showToast } from "../../components/common/notification/Notification";
import { usePropertyApiClient } from "../property/services/usePropertyApiClient";
import { createLeaseService } from "./services/leaseService";
import { getLeasesHome } from "./constants/leaseRoutes";
import AIDescriptionAssist from "../../components/common/ai/AIDescriptionAssist";

const FORM_ID = "lease-form";

// Same fields and rules as the former Add / Edit Lease popup
const validationSchema = Yup.object({
  title: Yup.string().trim().required("Title is required"),
  purposeId: Yup.string().required("Purpose is required"),
  price: Yup.number().typeError("Budget must be a number").min(0, "Budget cannot be negative").required("Budget is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
});

const LeaseFormBody = ({ purposes, states, cities, onStateSelected, saved }) => {
  const { dirty, values, setFieldValue } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);

  const purposeOptions = useMemo(() => purposes.map((p) => ({ value: String(p.id), label: p.name })), [purposes]);
  const stateOptions = useMemo(() => states.map((s) => ({ value: s.name, label: s.name, id: s.id })), [states]);
  const cityOptions = useMemo(() => cities.map((c) => ({ value: c.name, label: c.name })), [cities]);

  return (
    <Form id={FORM_ID} noValidate>
      <FocusFirstError />

      <Section title="Lease details" hint="What are you looking to lease?">
        <div className="fk-grid">
          <TextField name="title" label="Title" required placeholder="Enter title" className="fk-col-full" />
          <SelectField name="purposeId" label="Purpose" required options={purposeOptions} placeholder="Select purpose" />
          <CurrencyField name="price" label="Budget" required placeholder="Enter budget" />
        </div>
      </Section>

      <Section title="Location">
        <div className="fk-grid">
          <SelectField
            name="state"
            label="State"
            required
            options={stateOptions}
            placeholder="Select state"
            onSelect={(value, option) => {
              setFieldValue("city", "");
              onStateSelected(option.id);
            }}
          />
          <SelectField
            name="city"
            label="City"
            required
            options={cityOptions}
            disabled={!values.state}
            helper={!values.state ? "Select a state first" : undefined}
            placeholder="Select city"
          />
        </div>
      </Section>

      <Section title="Description">
        <AIDescriptionAssist
          type="lease"
          values={{ ...values, purposeName: purposeOptions.find((o) => o.value === values.purposeId)?.label }}
          value={values.description}
          onChange={(text) => setFieldValue("description", text)}
          showCounter
        >
          <TextAreaField name="description" label="About this lease" placeholder="Describe your requirement" />
        </AIDescriptionAssist>
      </Section>
    </Form>
  );
};

/** /{customer|partner}/leases/new and /…/leases/:Id/edit (replaces the Add / Edit Lease popup) */
const LeaseFormPage = () => {
  const { Id } = useParams();
  const isEdit = Boolean(Id);
  const navigate = useNavigate();
  const location = useLocation();
  const LEASES_HOME = getLeasesHome(location.pathname);
  const exit = useExitWizard(LEASES_HOME);

  const apiClient = usePropertyApiClient();
  const leaseService = useMemo(() => createLeaseService(apiClient), [apiClient]);

  const [purposes, setPurposes] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadCities = async (stateId) => {
    setCities(stateId ? await leaseService.fetchCities(stateId) : []);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [purposeList, stateList] = await Promise.all([leaseService.fetchPurposes(), leaseService.fetchStates(1)]);
        if (cancelled) return;
        setPurposes(purposeList);
        setStates(stateList);

        if (isEdit) {
          // Use the lease handed over by the list, otherwise load it (direct link / refresh)
          let existing = location.state?.lease;
          if (!existing || String(existing.id) !== String(Id)) {
            const data = await leaseService.getById(Id).catch(() => null);
            existing = data?.lease || null;
          }
          if (cancelled) return;
          if (!existing) {
            setNotFound(true);
          } else {
            setLease(existing);
            const match = stateList.find((s) => s.name === existing.state);
            if (match) await loadCities(match.id);
          }
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.text || err?.message || "Unable to load the form.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Id, leaseService]);

  const initialValues = {
    title: lease?.title || "",
    description: lease?.description || "",
    price: lease?.budget ?? "",
    state: lease?.state || "",
    city: lease?.city || "",
    purposeId: lease?.purpose?.id ? String(lease.purpose.id) : "",
  };

  const handleSubmit = async (values) => {
    const purpose = purposes.find((p) => String(p.id) === values.purposeId);
    const payload = {
      id: lease?.id,
      title: values.title.trim(),
      description: values.description.trim(),
      price: values.price,
      city: values.city,
      state: values.state,
      purpose,
    };
    setSubmitting(true);
    try {
      if (isEdit) await leaseService.edit(payload);
      else await leaseService.create(payload);
      showToast(isEdit ? "Lease updated successfully" : "Lease added successfully", { type: "success" });
      setSaved(true); // lifts the unsaved-changes warning before leaving
      allowLeaveOnce();
      setTimeout(() => navigate(LEASES_HOME), 0);
    } catch (err) {
      // Form data stays as entered
      showToast(err?.response?.data?.text || err?.message || "Failed to save lease", { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="fk-page" style={{ maxWidth: 1200 }}>
        <div className="alert alert-warning d-flex align-items-center justify-content-between" role="alert">
          <span>This lease could not be found. It may have been removed.</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate(LEASES_HOME)}>
            Back to My Leases
          </button>
        </div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="fk-page" style={{ maxWidth: 1200 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <FormPageLayout
      title={isEdit ? "Edit Lease" : "Add Lease"}
      subtitle={isEdit ? "Update your lease details." : "Add a lease requirement to your account."}
      formId={FORM_ID}
      submitLabel={isEdit ? "Save Changes" : "Add Lease"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        <LeaseFormBody purposes={purposes} states={states} cities={cities} onStateSelected={loadCities} saved={saved} />
      </Formik>
    </FormPageLayout>
  );
};

export default LeaseFormPage;
